import { useEffect, useState } from "react";
import { api, AUD, PCT } from "../lib/api";
import { CaretRight, TrendUp, ArrowUpRight } from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import { isDemoMode } from "../lib/demoStore";

// Config per entity so the dashboard can be rendered from data alone.
// New/unknown entities fall back to sensible defaults.
const ENTITY_META = {
    "Personal":        { note: "Home, vehicles, personal cash & loans", to: "/balance-sheet", tid: "entity-personal" },
    "Pendaz":          { name: "Pendaz Business", note: "Cash, shares, GBE equity & receivables", to: "/pendaz", tid: "entity-pendaz" },
    "Family Business": { note: "Business cash, equity & receivables",   to: "/balance-sheet", tid: "entity-family-business" },
    "Super Fund":      { note: "Retail / industry super accumulation",  to: "/balance-sheet", tid: "entity-super-fund" },
    "SMSF":            { note: "Portfolios & cash under the fund",       to: "/smsf", tid: "entity-smsf" },
};

function KPI({ eyebrow, value, sub, tid, tone = "ink" }) {
    return (
        <div className="hairline bg-white p-8 rounded" data-testid={tid}>
            <div className="label-eyebrow mb-4">{eyebrow}</div>
            <div className="font-serif text-4xl lg:text-5xl leading-none tracking-tight font-mono-num"
                 style={{ color: tone === "green" ? "var(--eucalyptus)" : "var(--ink)" }}>
                {value}
            </div>
            {sub && <div className="mt-4 text-sm text-[var(--muted)]">{sub}</div>}
        </div>
    );
}

function EntityCard({ name, value, note, to, tid }) {
    return (
        <Link to={to} className="hairline bg-white p-7 rounded flex flex-col hover:bg-black/[0.02] transition-colors" data-testid={tid}>
            <div className="flex items-center justify-between mb-6">
                <div className="label-eyebrow">{name}</div>
                <ArrowUpRight size={16} className="text-[var(--muted)]" />
            </div>
            <div className="font-serif text-3xl tracking-tight font-mono-num">{AUD(value)}</div>
            <div className="text-sm text-[var(--muted)] mt-2">{note}</div>
        </Link>
    );
}

export default function Dashboard() {
    const [d, setD] = useState(null);
    useEffect(() => {
        api.get("/snapshot").then(r => setD(r.data));
        // Fire-and-forget weekly auto-sync — endpoint self-dedupes to at most
        // once per 6 days, so it's safe to call on every dashboard load.
        // Ignored errors (e.g. accountant role 403) are intentional.
        api.post("/scheduled/weekly-portfolio-sync").catch(() => {});
    }, []);
    if (!d) return <div className="text-[var(--muted)]">Loading snapshot…</div>;

    const budgetRatio = d.annual_lifestyle_actual / (d.annual_lifestyle_budget || 1);
    const budgetStatus = budgetRatio > 1.2 ? "Review" : budgetRatio > 1.0 ? "Watch" : "On Plan";
    const chip = budgetStatus === "On Plan" ? "status-on-plan" : budgetStatus === "Watch" ? "status-watch" : "status-review";

    return (
        <div>
            <div className="mb-10">
                <div className="label-eyebrow mb-3">Family Financial Snapshot</div>
                <h1 className="font-serif text-5xl lg:text-6xl tracking-tight leading-none">
                    Where we stand today.
                </h1>
                <p className="text-[var(--muted)] mt-4 max-w-2xl">
                    {isDemoMode()
                        ? "A discreet, plain-English view of the household — personal, business (if any) and SMSF entities, with lifestyle spending and cash flow."
                        : "A discreet, plain-English view of Darren & Penny's wealth — Personal, Pendaz business and SMSF — with lifestyle spending and cash flow."}
                </p>
            </div>

            <div className="grid md:grid-cols-3 gap-6 mb-8">
                <KPI eyebrow="Total Family Wealth" value={AUD(d.family_wealth)}
                     sub={isDemoMode() ? "Home, vehicles, super, business and SMSF combined." : "Home, vehicles, Pendaz and SMSF combined."}
                     tid="kpi-family-wealth" tone="green" />
                <KPI eyebrow="Financial Assets" value={AUD(d.financial_assets)}
                     sub={isDemoMode() ? "Super, SMSF & business — liquid and invested." : "Pendaz + SMSF liquid & invested."}
                     tid="kpi-financial-assets" />
                <KPI eyebrow="Annual Lifestyle Budget" value={AUD(d.annual_lifestyle_budget)}
                     sub={`FY26 actual: ${AUD(d.annual_lifestyle_actual)}`} tid="kpi-lifestyle-budget" />
            </div>

            <div className="grid md:grid-cols-4 gap-6 mb-12">
                {Object.entries(d.per_entity || {})
                    .filter(([, v]) => Number(v) !== 0)
                    .map(([key, value]) => {
                        const meta = ENTITY_META[key] || { note: "", to: "/balance-sheet", tid: `entity-${key.toLowerCase().replace(/\s+/g,'-')}` };
                        return (
                            <EntityCard key={key} name={meta.name || key} value={value}
                                        note={meta.note} to={meta.to} tid={meta.tid} />
                        );
                    })}
                <div className="hairline bg-white p-7 rounded flex flex-col" data-testid="entity-lifestyle">
                    <div className="flex items-center justify-between mb-6">
                        <div className="label-eyebrow">Lifestyle Status</div>
                        <span className={`chip ${chip}`} data-testid="lifestyle-status-chip">{budgetStatus}</span>
                    </div>
                    <div className="font-serif text-3xl tracking-tight font-mono-num">{PCT(budgetRatio, 0)}</div>
                    <div className="text-sm text-[var(--muted)] mt-2">of annual budget spent (FY26)</div>
                </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
                <div className="hairline bg-white rounded p-8">
                    <div className="label-eyebrow mb-4">Quick Actions</div>
                    <div className="space-y-2">
                        <QuickLink to="/transactions" label="Import a bank CSV" tid="q-import" />
                        <QuickLink to="/lifestyle" label="Review lifestyle spending" tid="q-lifestyle" />
                        <QuickLink to="/retirement" label="Model a retirement scenario" tid="q-retirement" />
                        <QuickLink to="/insights" label="Generate monthly board paper" tid="q-board" />
                    </div>
                </div>
                <div className="hairline bg-white rounded p-8">
                    <div className="label-eyebrow mb-4 flex items-center gap-2">
                        <TrendUp size={14} /> Monthly Ritual
                    </div>
                    <ol className="space-y-3 text-sm text-[var(--ink)] list-decimal ml-5">
                        <li>{isDemoMode() ? "Import last month's bank & credit card CSVs." : "Import last month's Westpac, Amex & Macquarie CSVs."}</li>
                        <li>Review &quot;Needs Review&quot; transactions.</li>
                        <li>Refresh {isDemoMode() ? "super, SMSF & business" : "Pendaz & SMSF"} valuations.</li>
                        <li>Read the AI-generated Board Paper.</li>
                        <li>Archive a snapshot of this month's numbers.</li>
                    </ol>
                </div>
            </div>
        </div>
    );
}

function QuickLink({ to, label, tid }) {
    return (
        <Link to={to} data-testid={tid}
              className="flex items-center justify-between py-2.5 border-b border-[var(--line)] last:border-none group">
            <span className="text-sm">{label}</span>
            <CaretRight size={14} className="opacity-50 group-hover:opacity-100 group-hover:translate-x-0.5 transition" />
        </Link>
    );
}
